import * as langsHelpers from 'globals/langs';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import * as dateTime from 'globals/datetime';
import * as utils from 'globals/utils';
import head from 'lodash/head';
import { saveCookie } from 'globals/cookieHelper';

import RealTimeConnection from './connect';

import store from '../../sdk/store';

import { responseToSessionList, responseToSession } from './views/session';
import { responseToSessionState } from './views/sessionState';

import { responseToCustomerStats } from './views/customer_stats';

import { responseToMessage, responseToMessageList } from './views/message';

import { responseToUser, responseToUserList } from './views/user';

import { responseToCompanyWithHours } from './views/company';
import { responseToClientActivity } from './views/activity';

import { responseToTeamList } from './views/teams';

import { responseDataToAttachment } from './views/attachment';

import { responseToSatisfactionForm } from './views/satisfactionForm';

import * as actionsRouting from '../modules/chat/app/ChatApp/actions_routing';

import {
  handleStartApp,
  handleRestartApp,
  handleOpenApp,
  handleOpenAppAndSession,
  handleCloseApp,
  handleStopApp,
  setAppStylesRTL,
  setAppOnline,
  setAppOffline,
  maybeConnectAndFetchSessions,
  getCompanySettings,
} from '../modules/app/App/actions';
import { handleSendNewMessage } from '../modules/chat/conversation/ChatConversationInput/actions';

import { updateLastActivity, updateSocketActivity } from '../modules/chat/app/ChatApp/actions';
import * as chatAppSelectors from '../modules/chat/app/ChatApp/selectors';
import * as vcTimerHelpers from '../modules/chat/conversation/ChatConversationVolumeControlInput/helpers';

import { responseToForm, responseToFormPost } from './views/forms';
import { responseToQueue } from './views/queue';
import Poller from './connect/poller';

export const DEFAULT_MESSAGES_PAGE_SIZE = 100;

export const MAINTAIN_ACTIVE_CHAT_THRESHOLD = dateTime.ONE_MINUTE * 5;

/* eslint-disable no-console */

class AppAPI {
  constructor(api) {
    this.api = api;
    this._eventListeners = {};

    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  get store() {
    return require('../redux/store').default; // eslint-disable-line global-require
  }

  get orgId() {
    return this.api.org.id;
  }

  get orgName() {
    return this.api.org.name;
  }

  get orgBaseUrl() {
    return this.api.orgBaseURL;
  }

  get trackingToken() {
    return this.api.trackingToken;
  }

  get trackingId() {
    return this.api.trackingId;
  }

  get customerId() {
    return this.api.customerId;
  }

  set customerId(customerId) {
    this.api.customerId = customerId;
  }

  get customerVerified() {
    return this.api.customerVerified;
  }

  init() {
    return this.api.init().then(() => {
      this.disconnect();
      this.store.dispatch(handleStartApp());
    });
  }

  start(options, cb) {
    this.store.dispatch(handleStartApp(cb));
  }

  stop() {
    this.store.dispatch(handleStopApp());
  }

  open({ icon } = {}) {
    if (icon === true) {
      saveCookie('noIcon', false);
    } else if (icon === false) {
      saveCookie('noIcon', true);
    }
    this.store.dispatch(handleOpenApp());
  }

  openToSession(sessionId) {
    this.store.dispatch(handleOpenAppAndSession(sessionId));
  }

  close() {
    this.store.dispatch(handleCloseApp());
  }

  on(event, callback) {
    if (!this._eventListeners[event]) {
      this._eventListeners[event] = [];
    }
    this._eventListeners[event].push(callback);
  }

  broadcast(event, ...data) {
    if (this._eventListeners[event]) {
      this._eventListeners[event].forEach(callback => {
        callback(...data);
      });
    }
  }

  connect({ onConnect, onMessage, onDisconnect, lastActivity }) {
    if (this.rtc) return;
    const pollingEnabled = false;
    this.rtc = new RealTimeConnection({ api: this, onConnect, onMessage, onDisconnect, lastActivity, pollingEnabled });
    this.rtc.start();
  }

  subscribeToChatActivityChannel() {
    const state = this.store.getState();
    const dispatch = this.store.dispatch;

    const showTypingIndicatorWeb = chatAppSelectors.getCompanyShowTypingIndicatorWeb(state);
    const showTypingIndicatorCustomerWeb = chatAppSelectors.getCompanyShowTypingIndicatorCustomerWeb(state);
    const activeSessionId = chatAppSelectors.getActiveSessionId(state);

    const shouldSubscribe = showTypingIndicatorCustomerWeb || showTypingIndicatorWeb;

    if (activeSessionId === 'new' || !shouldSubscribe) return Promise.resolve();

    return this.rtc.joinChatActivityChannel(activeSessionId, (eventName, data) => {
      return dispatch(updateSocketActivity(eventName, data));
    });
  }

  unsubscribeFromChatActivityChannel() {
    if (this.rtc) {
      this.rtc.leaveAndResetChatActivityChannel();
    }
  }

  // eslint-disable-next-line
  sendToChatActivityChannel(status, eventName) {
    const state = this.store.getState();

    const showTypingIndicatorWeb = chatAppSelectors.getCompanyShowTypingIndicatorWeb(state);

    if (!showTypingIndicatorWeb) return Promise.resolve();

    const activeSessionId = chatAppSelectors.getActiveSessionId(state);

    const params = {
      sessionId: activeSessionId,
      customerId: this.customerId,
      status,
      eventName,
    };

    if (this.rtc) {
      this.rtc.sendChatActivity(params);
    }
  }

  startStatsPoller({ onCallStats }) {
    if (this.statsPoller) return;
    this.statsPoller = new Poller({
      task: () => onCallStats(),
      defaultInterval: dateTime.ONE_MINUTE,
      skipFirstPoll: true,
    });
    this.statsPoller.start();
  }

  disconnect() {
    if (this.rtc) {
      this.rtc.stop();
      this.rtc = null;
    }
  }

  handleOnline() {
    this.store.dispatch(setAppOnline());
    this.store.dispatch(maybeConnectAndFetchSessions());
  }

  handleOffline() {
    this.store.dispatch(setAppOffline());
    vcTimerHelpers.pauseVcTimers();
  }

  hasCustomerId() {
    return !!this.customerId;
  }

  identify(...args) {
    this.api.identify(...args).then(() => {
      this.disconnect();
      this.store.dispatch(handleRestartApp());
    });
  }

  trackEvent() {
    return this.api.trackEvent();
  }

  clearTracking(cb) {
    return this.api.clearTracking().then(() => {
      this.disconnect();
      this.store.dispatch(handleRestartApp(cb));
    });
  }

  describe(...args) {
    return this.api.describe(...args);
  }

  describeNext(...args) {
    return this.api.describeNext(...args);
  }

  describeEmail(email) {
    return this.describe({ email });
  }

  flushPendingDescribeNexts() {
    return this.api.flushPendingDescribeNexts('conversation');
  }

  createSession(title) {
    const data = { title };
    this.ensureRecentConnection();
    return this.api.sdkPost('chat/sessions', data).then(responseToSession);
  }

  getChatSession(sessionId) {
    return this.getChatSessions().then(sessions => {
      const session = sessions.find(x => x.id === sessionId);
      if (!session) throw Error('Unable to find session');
      return session;
    });
  }

  getSessionState() {
    return this.api.sdkGet('chat/states/current').then(responseToSessionState);
  }

  updateSessionState(data) {
    return this.api.sdkPut('chat/states/current', data).then(responseToSessionState);
  }

  getUsers(userIdsArray) {
    if (!userIdsArray) return Promise.resolve([]);

    // HACK: don't make a request for the team user
    const filtered = userIdsArray.filter(userId => userId !== '__team');
    if (filtered.length === 0) return Promise.resolve([]);

    return this.api.sdkGetWithCache(`users/${filtered.join(',')}`).then(responseToUserList);
  }

  getUser(userId) {
    // HACK: don't make a request for the team user
    if (!userId || userId === '__team') return null;
    return this.api.sdkGetWithCache(`users/${userId}`).then(responseToUser);
  }

  getChatSessions(withoutCache) {
    if (withoutCache) {
      return this.api.sdkGet('chat/sessions').then(responseToSessionList);
    }

    return this.api.sdkGetWithCache('chat/sessions').then(responseToSessionList);
  }

  getCustomerStats(dispatchOnSuccess = true) {
    return this.api
      .sdkGetWithCache('chat/customers/stats')
      .then(responseToCustomerStats)
      .then(stats => {
        if (dispatchOnSuccess) this.store.dispatch(updateLastActivity(stats.activity));
        return stats;
      });
  }

  getSchedule({ id = undefined, attemptRefetchForDefault = true }) {
    const idToFetch = id || 'default';

    // eslint-disable-next-line
    return this.api.sdkGetWithCache(`schedules/${idToFetch}?include=holidays`).catch(reason => {
      const status = reason && reason.response && reason.response.status;
      if ((status === 404 || status === 400) && attemptRefetchForDefault) {
        this.getSchedule({ attemptRefetchForDefault: false });
        return undefined;
      }
    });
  }

  getCurrentCompanySettings() {
    const schedule = this.getScheduleOverride();

    return Promise.all([this.api.sdkGetWithCache('chat/settings'), this.getSchedule({ id: schedule })])
      .then(([companyResponse, hoursResponse]) => responseToCompanyWithHours(companyResponse, hoursResponse))
      .then(company => {
        const companyWithName = company;
        if (!company.name) {
          companyWithName.name = this.orgName[0].toUpperCase() + this.orgName.substr(1);
        }

        return companyWithName;
      });
  }

  getMessagesForSession(session, page = 1) {
    const paginationQuery = `page=${page}&pageSize=${DEFAULT_MESSAGES_PAGE_SIZE}`;
    return this.api
      .sdkGetWithCache(`chat/sessions/${session.id}/messages?${paginationQuery}`)
      .then(responseToMessageList)
      .then(messages => {
        if (!messages || messages.length === 0) {
          return [];
        }
        return messages;
      });
  }

  getMessagesForMultiSession(sessions) {
    return this.api.all(
      sessions.map(session =>
        this.getMessagesForSession(session).then(messages => ({ sessionId: session.id, messages })),
      ),
    );
  }

  getChatForm(id) {
    return this.api
      .sdkGet(`chat/forms/${id}`)
      .then(responseToForm)
      .catch(reason => {
        const status = get(reason, 'response.status');
        const errors = get(reason, 'response.data.errors');
        const error = head(errors);
        const code = get(error, 'code');
        const parameter = get(error, 'source.parameter');

        if (status === 400) {
          if (code === 'badparam') {
            console.error(`There was an error fetching the form. You have entered an invalid ${parameter}.`);
          } else {
            console.error('There was an error fetching the form.');
          }
          return {};
        } else if (status === 404) {
          console.error(
            'There was an error fetching the form. Please check if the form still exists in your Chat Assistant Settings. https://help.kustomer.com/chat-conversational-assistant-B1b2rXYXz',
          );
          return {};
        }

        throw reason;
      });
  }

  getTeams(ids) {
    return this.api.sdkGetWithCache(`chat/teams/${ids}`).then(responseToTeamList);
  }

  sendClientActivity(secondsOnPage) {
    const reqOpts = {
      currentPageSeconds: secondsOnPage,
      previousPage: document.referrer,
    };
    return this.api.sdkPost('client-activity', reqOpts);
  }

  initClientActivity() {
    return this.sendClientActivity(0)
      .then(responseToClientActivity)
      .then(activity => {
        const uniqueIntervals = uniqBy(activity.intervals, 'seconds');
        uniqueIntervals.forEach(interval => {
          setTimeout(() => this.sendClientActivity(interval.seconds), interval.seconds * 1000);
        });
        return activity;
      });
  }

  submitForm(id, data) {
    this.ensureRecentConnection();
    return this.api.sdkPost(`chat/forms/${id}/responses`, data).then(responseToFormPost);
  }

  markChatSessionSeen(sessionId) {
    return this.api
      .sdkPut(`chat/sessions/${sessionId}`, { lastSeenAt: new Date().toISOString() })
      .then(responseToSession);
  }

  refreshTrackingToken() {
    return this.api.getCurrentTrackingToken();
  }

  fetchChatMessageById({ messageId, sessionId }) {
    return this.api.sdkGetWithCache(`chat/sessions/${sessionId}/messages/${messageId}`).then(responseToMessage);
  }

  sendChatMessage(message) {
    return this.api.sdkPost('chat/messages', message).then(responseToMessage);
  }

  setActiveSession(session) {
    this.api.activeConversation = session;
  }

  submitVCForm(data) {
    return this.api.sdkPost('chat/volume-control/responses', data).then(responseToFormPost);
  }

  markConversationDone(sessionId, lockReason) {
    return this.api.sdkPut(`chat/sessions/${sessionId}`, { locked: true, lockReason }).then(responseToSession);
  }

  createAttachment(data) {
    return this.api.sdkPost('chat/attachments', data).then(responseDataToAttachment);
  }

  uploadAttachment(url, data) {
    // S3 upload returns a 203 with no content, so no view is necessary
    return this.api.sdkExternalPost(url, data);
  }

  fetchMessageAttachment(messageId, attachmentId, redirect = false) {
    return this.api
      .sdkGet(`chat/messages/${messageId}/attachments/${attachmentId}?redirect=${redirect}`)
      .then(responseDataToAttachment);
  }

  createMessageWithAttachment(data) {
    return this.api.sdkPost('chat/messages', data).then(responseToMessage);
  }

  getQueueWaitTime(sessionId) {
    return this.api.sdkGet(`chat/sessions/${sessionId}/queue`).then(responseToQueue);
  }

  convertServerToLocalDate(timestamp = Date.now()) {
    const millis = new Date(timestamp).getTime() + this.api.millisAheadOfServer;
    return new Date(millis);
  }

  convertLocalToServerDate(timestamp = Date.now()) {
    const millis = new Date(timestamp).getTime() - this.api.millisAheadOfServer;
    return new Date(millis);
  }

  shouldMaintainActiveChats(activity) {
    const convertedActivity = this.convertServerToLocalDate(activity).getTime();
    const millisSinceLastActivity = utils.getMillisSinceLastActivity(convertedActivity);
    return millisSinceLastActivity < MAINTAIN_ACTIVE_CHAT_THRESHOLD;
  }

  isRTCAlive() {
    return get(this, 'rtc.connected');
  }

  isChatAvailable() {
    const state = this.store.getState();
    const company = chatAppSelectors.getCompany(state);
    if (!company.hours) return Promise.reject(new Error('company hours not found'));
    const { hours, enabled } = company;
    const inBusinessHours = utils.isNowInsideBillingHours(hours);
    return Promise.resolve(inBusinessHours && enabled);
  }

  openConversationsCount() {
    const state = this.store.getState();
    const activeSessionCount = chatAppSelectors.getActiveSessionCount(state);
    return Promise.resolve(activeSessionCount);
  }

  ensureRecentConnection() {
    if (this.rtc) this.rtc.refresh();
  }

  setWidgetToRTL() {
    try {
      document.querySelector('.kustomer').dir = 'rtl';
      this.store.dispatch(setAppStylesRTL());
    } catch (e) {
      // noop
    }
  }

  setLangOverride(lang) {
    store.set('lang', lang);
    if (langsHelpers.isRTL(lang)) {
      this.setWidgetToRTL();
    }
  }

  getLangOverride() {
    return store.get('lang') || undefined;
  }

  isLangOverrideRTL() {
    const override = this.getLangOverride();
    return langsHelpers.isRTL(override);
  }

  removeLangOverride() {
    store.remove('lang');
  }

  setFormOverride(formId) {
    store.set('form', formId);
  }

  getFormOverride() {
    return store.get('form') || undefined;
  }

  removeFormOverride() {
    const hasOverride = this.getFormOverride();

    if (!hasOverride) return;

    store.remove('form');
  }

  setTempFormOverride(formId) {
    store.set('tempForm', formId);
  }

  getTempFormOverride() {
    return store.get('tempForm') || undefined;
  }

  removeTempFormOverride() {
    const hasOverride = this.getTempFormOverride();

    if (!hasOverride) return;

    store.remove('tempForm');
  }

  getScheduleOverride() {
    return store.get('schedule') || undefined;
  }

  getSatisfactionForm(sessionId) {
    return this.api.sdkPost(`chat/sessions/${sessionId}/satisfaction`, {}).then(responseToSatisfactionForm);
  }

  submitSatisfactionResponse(responseId, data) {
    return this.api.sdkPut(`chat/satisfaction-responses/${responseId}`, data);
  }

  openAndSendMessage(messageData, attributes, { icon, form } = {}) {
    if (!messageData) return Promise.reject(Error('Missing message object'));
    if (!messageData.body) return Promise.reject(Error("Missing message 'body'"));

    if (icon === true) {
      saveCookie('noIcon', false);
    } else if (icon === false) {
      saveCookie('noIcon', true);
    }

    let cleanedMessageData = Object.assign({}, messageData);

    const cleanBody = utils.cleanBody(messageData.body);
    if (!cleanBody) return Promise.reject(Error("Message 'body' is invalid"));

    cleanedMessageData = Object.assign({}, cleanedMessageData, { body: cleanBody });

    return this.store.dispatch(getCompanySettings()).then(settings => {
      if (!settings.enabled) return Promise.reject(Error('Chat is disabled'));

      const display = settings.offhoursDisplay;
      const isInsideBusinessHours = utils.isNowInsideBillingHours(settings.hours);

      if (!isInsideBusinessHours && display === 'none') return Promise.reject(Error('Chat is hidden'));
      if (!isInsideBusinessHours && display === 'offline') return Promise.reject(Error('Chat is offline'));

      if (attributes) this.describeNext('conversation', attributes);

      if (form) {
        return this.store.dispatch(actionsRouting.handleCreateNewConversation(cleanBody)).then(() => {
          return this.open();
        });
      }

      return this.store.dispatch(handleSendNewMessage(cleanedMessageData)).then(session => {
        if (session && session.id) {
          this.openToSession(session.id);
        }
      });
    });
  }
}

// The app needs to know when these methods are called
const Kustomer = window.Kustomer || { _q: [], _i: [] };
if (process.env.NODE_ENV === 'test') {
  Kustomer.API = require('../../sdk/api/'); // eslint-disable-line global-require
}

const AppAPISingleton = new AppAPI(Kustomer.API);

Kustomer.identify = (...args) => {
  return AppAPISingleton.identify(...args);
};

Kustomer.clear = (...args) => {
  return AppAPISingleton.clearTracking(...args);
};

Kustomer.track = (...args) => {
  return AppAPISingleton.trackEvent(...args);
};

Kustomer.describe = (...args) => {
  return AppAPISingleton.describe(...args);
};

Kustomer.describeCustomer = (...args) => {
  return AppAPISingleton.describe('customer', ...args);
};

Kustomer.describeConversation = (...args) => {
  return AppAPISingleton.describe('conversation', ...args);
};

Kustomer.describeNextConversation = (...args) => {
  return AppAPISingleton.describeNext('conversation', ...args);
};

Kustomer.start = (options, cb, ...args) => {
  const formId = get(options, 'form');
  const lang = get(options, 'lang');

  if (!options || options.icon) {
    saveCookie('noIcon', false);
  } else {
    saveCookie('noIcon', true);
  }

  if (lang) {
    AppAPISingleton.setLangOverride(lang);
  } else if (lang === null) {
    AppAPISingleton.removeLangOverride();
  }

  if (formId) {
    AppAPISingleton.setFormOverride(formId);
  } else {
    AppAPISingleton.removeFormOverride();
  }

  return AppAPISingleton.start(options, cb, ...args);
};

Kustomer.stop = (...args) => {
  return AppAPISingleton.stop(...args);
};

Kustomer.open = (...args) => {
  return AppAPISingleton.open(...args);
};

Kustomer.openAndSendMessage = (...args) => {
  const options = get(args, 2);

  const form = options && options.form;

  if (form) {
    AppAPISingleton.setTempFormOverride(form);
  } else {
    AppAPISingleton.removeTempFormOverride();
  }

  return AppAPISingleton.openAndSendMessage(...args);
};

Kustomer.isChatAvailable = () => {
  return AppAPISingleton.isChatAvailable();
};

Kustomer.openConversationsCount = () => {
  return AppAPISingleton.openConversationsCount();
};

Kustomer.close = (...args) => {
  return AppAPISingleton.close(...args);
};

Kustomer.on = (event, callback) => {
  return AppAPISingleton.on(event, callback);
};

export default AppAPISingleton;
